import { Typography, Grid2, Tooltip, Box } from '@mui/material';
import { ReactNode, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Flex, FlexCenVer, FlexColCenVer, FlexCol } from '@/UI/styles';
import { TextSmRg, TextStdMd } from '@/UI/AppTypography/AppTypography';
import { formatNumberWithCommas } from '@/utils/numbers';
import { AsteriskIcon } from '@/UI/Icons/AsteriskIcon';
import { PlusIcon } from '@/UI/Icons/PlusIcon';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { PermissionsContainer } from '@/components/PermissionsContainer/PermissionsContainer';
import { SubscriptionType } from '@/types/shared';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { CreditsContext } from '@/services/CreditsContext';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { ButtonTextPlain } from '@/UI/ButtonTextPlain/ButtonTextPlain';
import { Label } from '@/UI/Label/Label';
import { AppSwitch } from '@/UI/AppSwitch/AppSwitch';
import { Input } from '@/UI/Input/Input';
import { FF_CREDIT_THRESHOLD_ALERTS } from '@/consts/featureFlags';
import { CreditsReportModal } from './CreditsReportModal';
import { useSubscriptionCredits } from './hooks/useSubscriptionCredits';
import { useCreditThresholdAlerts } from './hooks/useCreditThresholdAlerts';

export const CreditsSection = () => {
  const { t } = useTranslation();
  const { isSubscriptionTypeIn, isWorkspaceAdmin } = useSubscriptionPermissions();
  const { handleGetMoreCreditsClick, workspaceCredits } = useContext(CreditsContext);
  const [isCreditsReportModalOpen, setIsCreditsReportModalOpen] = useState(false);
  const { isEnterpriseSubscription, totalRolloverCredits, totalExtraCredits, availableMonthlyCredits } =
    useSubscriptionCredits();
  const {
    enabled: creditThresholdAlertsEnabled,
    thresholdInputValue,
    thresholdInputRef,
    handleCreditThresholdToggle,
    handleThresholdInputChange,
    handleCreditThresholdChange,
    handleCreditThresholdCancel,
  } = useCreditThresholdAlerts();
  const isCreditThresholdAlertsFeatureEnabled = useFeatureFlagEnabled(FF_CREDIT_THRESHOLD_ALERTS);
  const shouldShowLowCreditsAlertsSection =
    isWorkspaceAdmin &&
    isCreditThresholdAlertsFeatureEnabled &&
    isSubscriptionTypeIn([SubscriptionType.Team, SubscriptionType.Enterprise]);

  const hasStats = !isEnterpriseSubscription || !isNil(totalRolloverCredits) || !isNil(totalExtraCredits);

  const getCreditsCtaEl = (): ReactNode => {
    const isDisabled = isSubscriptionTypeIn([SubscriptionType.Free]);

    return (
      <PermissionsContainer permission="credits" type="action">
        <Tooltip
          title={isDisabled ? t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CREDITS.UPGRADE_CTA_FREE_TOOLTIP) : null}
          placement="top"
        >
          <span>
            <ButtonContained
              disabled={isDisabled}
              onClick={handleGetMoreCreditsClick}
              sx={{ gap: 0.5 }}
              mode="filled-light-secondary"
              size="xs"
            >
              <PlusIcon width={16} height={16} />
              {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CREDITS.UPGRADE_CTA)}
            </ButtonContained>
          </span>
        </Tooltip>
      </PermissionsContainer>
    );
  };

  return (
    <>
      <Grid2 container display="grid" direction="row" gridTemplateColumns="1fr 1.5fr 1fr" gap={4}>
        <Typography variant="body-lg-md">{t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CREDITS_TITLE)}</Typography>
        <FlexColCenVer>
          {workspaceCredits !== null ? (
            <FlexCol sx={{ gap: 0.5 }}>
              <Label>{t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CURRENT_AMOUNT)}</Label>
              <FlexCenVer>
                <AsteriskIcon width={24} height={24} />
                <Typography variant="h2" sx={{ display: 'flex', flexDirection: 'column', fontWeight: 400, ml: 0.5 }}>
                  {formatNumberWithCommas(workspaceCredits)}
                </Typography>
              </FlexCenVer>
            </FlexCol>
          ) : null}
          {hasStats && (
            <FlexColCenVer sx={{ pt: 1, mt: 1.5 }}>
              {!isEnterpriseSubscription && (
                <>
                  <Typography variant="body-std-md">
                    {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.REMAINING_MONTHLY_CREDITS_TEXT, {
                      availableMonthlyCredits: formatNumberWithCommas(availableMonthlyCredits || 0),
                    })}
                  </Typography>
                </>
              )}
              {!isNil(totalRolloverCredits) && (
                <Typography variant="body-std-md">
                  {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.ROLLOVER_CREDITS_TEXT, {
                    totalRolloverCredits: formatNumberWithCommas(totalRolloverCredits),
                  })}
                </Typography>
              )}
              {!isNil(totalExtraCredits) && (
                <Typography variant="body-std-md">
                  {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.EXTRA_CREDITS_TEXT, {
                    totalExtraCredits: formatNumberWithCommas(totalExtraCredits),
                  })}
                </Typography>
              )}
            </FlexColCenVer>
          )}
          {isWorkspaceAdmin && (
            <Box sx={{ mt: 2.5 }}>
              <ButtonTextPlain
                variant="body-sm-rg"
                color={color.White80_T}
                onClick={() => setIsCreditsReportModalOpen(true)}
              >
                {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.DOWNLOAD_CREDITS_REPORT)}
              </ButtonTextPlain>
            </Box>
          )}
        </FlexColCenVer>
        <Flex sx={{ justifySelf: 'flex-end' }}>{getCreditsCtaEl()}</Flex>
        <CreditsReportModal open={isCreditsReportModalOpen} onClose={() => setIsCreditsReportModalOpen(false)} />
      </Grid2>
      {shouldShowLowCreditsAlertsSection && (
        <Grid2 container display="grid" direction="row" gridTemplateColumns="1fr 1.5fr 1fr" gap={4}>
          <Box />
          <FlexCol sx={{ gap: 0.5 }}>
            <TextStdMd>{t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CREDIT_THRESHOLD_ALERTS_TITLE)}</TextStdMd>
            <TextSmRg color={color.White64_T}>
              {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CREDIT_THRESHOLD_ALERTS_SUBTITLE)}
            </TextSmRg>
            {creditThresholdAlertsEnabled && (
              <Box sx={{ mt: 1 }}>
                <Input
                  inputRef={thresholdInputRef}
                  noAutoSelect
                  type="number"
                  startAdornment={<AsteriskIcon width={16} height={16} style={{ color: color.White80_T }} />}
                  value={thresholdInputValue}
                  onChange={(e) => handleThresholdInputChange((e.target as HTMLInputElement).valueAsNumber)}
                  noBg
                  sx={{ width: 200, '& .MuiInputBase-root': { gap: '4px !important' } }}
                  onBlur={(e) => void handleCreditThresholdChange((e.target as HTMLInputElement).valueAsNumber)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    if (e.key === 'Escape') handleCreditThresholdCancel();
                  }}
                  slotProps={{ input: { min: 0 } }}
                />
              </Box>
            )}
          </FlexCol>
          <Flex sx={{ justifySelf: 'flex-end' }}>
            <AppSwitch
              checked={creditThresholdAlertsEnabled}
              onChange={(e) => void handleCreditThresholdToggle(e.target.checked)}
              slotProps={{ input: { 'aria-label': 'low credits alerts' } }}
            />
          </Flex>
        </Grid2>
      )}
    </>
  );
};
